import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { ConstructionKeyType, KeyNodeType } from '@overtur-monorepo/keying-shared'
import { Component, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core'
import { take, takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { fadeIn } from '../../../animations/fadeIn'
import { fadeInOnly } from '../../../animations/fadeInOnly'
import { flip } from '../../../animations/flip'
import { ArrowDivDirective } from '../../../directives/arrow-div.directive'
import { KeySystem, ShearLines, Structure } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyBoardService } from '../../../services/keyboard.service'
import { KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { LoadingService } from '../../../services/loading.service'
import { ChangeLogConfirmationDialogComponent } from '../../matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { DialogService } from '../../../services/dialog.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { LoggingService } from '../../../services/logging.service'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { User } from '@overtur-monorepo/keying-user'

@Component({
  selector: 'app-shearline-input-array',
  templateUrl: './shearline-input-array.component.html',
  styleUrls: ['./shearline-input-array.component.scss'],
  animations: [fadeIn, flip, fadeInOnly],
  standalone: false,
})
export class ShearlineInputArrayComponent implements OnInit {
  keyNodeType = KeyNodeType
  destroy$ = new Subject()

  @Input() shearLines: ShearLines
  @Input() disabled = false
  @Input() selectedCylinder
  @Input() editing = false
  @Input() structure: Structure
  @Input() keySystem: KeySystem
  @Input() selectedMasterKey: MasterKey
  @Input() hasControlKeyMaster: boolean

  @Output() inputUpdates = new EventEmitter<any>()
  @Output() validateSaveShearline = new EventEmitter<any>()
  @Output() sortShearLines = new EventEmitter<any>()
  @Output() resetShearLines = new EventEmitter<any>()
  @Output() generateShearLines = new EventEmitter<any>()
  @Output() addRowEvent = new EventEmitter<any>()
  @Output() onEdit = new EventEmitter<any>()
  @Output() prepareForMECS = new EventEmitter<boolean>()

  @ViewChildren(ArrowDivDirective) inputs: QueryList<ArrowDivDirective>

  columns
  expanded = true
  autoEdit = false

  shouldDisableShearlines = false

  isGenerateShearlinesDisabled = false
  saveCylinderShearline = false
  validatedShearlines

  isSaveShearline = false
  user: User

  constructor(
    private keyboardService: KeyBoardService,
    private keyingCylinderService: KeyingCylinderService,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    private dialogService: DialogService,
    private keyingSystemService: KeyingSystemService,
    private keyingDataSetsService: KeyingDataSetsService,
    private loggingService: LoggingService,
    private keyingAuthService: KeyingAuthService
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.loggingService.log('Shearlines:', this.shearLines)
    this.columns = this.shearLines?.length

    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) return
      this.user = user
    })

    this.keyboardService.keyBoard.subscribe((res) => {
      this.move(res)
    })

    this.isGenerateShearlinesDisabled = this.displayShearlineButton()
    this.editing = false

    this.keyingCylinderService.saveCylindShearlines$.pipe(takeUntil(this.destroy$)).subscribe((sherlines) => {
      if (sherlines && this.isSaveShearline) {
        this.saveCylinderShearline = true
        this.validatedShearlines = sherlines
        if (this.validatedShearlines.chambers.every((x) => !x.hasErrors)) {
          this.checkIfCylinderIsPublished()
        }
        this.isSaveShearline = false
      }
    })
  }

  /* istanbul ignore next */
  ngOnChanges(changes: SimpleChanges) {
    this.toggleEditingControls(changes)
    this.toggleHideCheckbox()
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  toggleEditingControls(changes) {
    if (this.shearLines?.shearLineDepths?.length === 0) {
      this.editing = false
    }

    if (changes.shearLines && this.autoEdit) {
      this.editing = true
      this.autoEdit = false
    }
  }

  toggleHideCheckbox() {
    this.shouldDisableShearlines =
      this.editing &&
      this.structure?.combinatingRuleSetId === 1 &&
      !this.isConstructionTypeRck(this.keySystem?.constructionTypeId)
  }

  isConstructionTypeRck(constructionTypeId: number) {
    switch (constructionTypeId) {
      case ConstructionKeyType.RCKNoMaster:
      case ConstructionKeyType.RCKTMK:
      case ConstructionKeyType.RCKChanges: {
        return true
      }
    }
    return false
  }

  isConstructionTypeWafer(constructionTypeId: number) {
    switch (constructionTypeId) {
      case ConstructionKeyType.WAFERTMK:
      case ConstructionKeyType.WAFERCHANGES: {
        return true
      }
    }

    return false
  }

  shouldShowToggle(rule, chamberIndex: number): boolean {
    if (!rule.canEmpty) {
      return false
    }

    // Show for normal cases (existing logic)
    if (this.shouldDisableShearlines) {
      return true
    }

    // Show for special 6-column case (chambers 4,5 when conditions are met)
    return this.shouldShowDisabledToggleForLastTwoChambers(chamberIndex)
  }

  shouldDisableToggle(chamberIndex: number): boolean {
    // Only disable if the chamber is already empty
    const chamber = this.shearLines?.chambers?.[chamberIndex]
    if (!chamber) return false

    // Super users may re-enable the 5th chamber (index 4) under Rule 015
    if (this.user?.isSuperUser && chamberIndex === 4 && this.shouldShowDisabledToggleForLastTwoChambers(chamberIndex)) return false

    // If chamber is already empty and meets the special conditions, keep it disabled
    return chamber.isEmpty && this.shouldShowDisabledToggleForLastTwoChambers(chamberIndex)
  }

  private shouldShowDisabledToggleForLastTwoChambers(chamberIndex: number): boolean {
    const hasSixColumns = this.columns === 6 || this.shearLines?.length === 6
    const isCombinatineRuleSetOne = this.structure?.combinatingRuleSetId === 1
    const isLastTwoChambers = chamberIndex === 4 || chamberIndex === 5 // 0-based index for positions 5,6
    const isDealerSystem = this.keySystem?.isDealerKeySystem

    return hasSixColumns && isCombinatineRuleSetOne && isLastTwoChambers && this.editing && !isDealerSystem
  }

  /* istanbul ignore next */
  saveCylinder() {
    // this emit will validate the sherlines
    this.validateSaveShearline.emit()
    this.isSaveShearline = true
  }

  /* istanbul ignore next */
  private checkIfCylinderIsPublished() {
    const auditTrailRequest = {
      reasonCode: null,
      description: null,
    }
    if (this.selectedCylinder.isPublished || this.selectedCylinder.isLegacyPublished) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Cylinder Updated',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: this.selectedCylinder.name,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.saveCylinderShearlineData(result.auditTrailRequest)
        }
      })
    } else {
      this.saveCylinderShearlineData(auditTrailRequest)
    }
  }

  private saveCylinderShearlineData(auditTrailRequest) {
    this.loadingService.set({ isLoading: true, message: 'Saving Shear Line' })
    this.keyingCylinderService
      .putCylinderShearLines(
        this.structure.id,
        this.selectedCylinder.keySystemId,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id,
        true,
        this.selectedCylinder.shearLines,
        auditTrailRequest
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
          this.toggleEdit()
          this.prepareForMECS.emit(true)
          this.loadingService.set({ isLoading: false, message: '' })
          if (this.getKeySystemConditionally()) {
            this.keyingSystemService
              .getKeySystem(this.structure.id, this.selectedCylinder.keySystemId)
              .pipe(take(1))
              .subscribe(
                (keySystem) => {
                  this.keyingSystemService.selectKeySystem(keySystem)
                },
                (error) => {
                  this.errorService.throw(error)
                }
              )
          }
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  getKeySystemConditionally() {
    return this.keySystem.statusId === 13
  }

  /* istanbul ignore next */
  toggleEdit() {
    this.editing = !this.editing
    this.onEdit.emit()
    this.toggleHideCheckbox()
  }

  /* istanbul ignore next */
  trackByFn(index: any, item: any) {
    return index
  }

  /* istanbul ignore next */
  removeRow() {
    this.shearLines.shearLineDepths.shift()
    this.onChange()
  }

  /* istanbul ignore next */
  addRow() {
    this.addRowEvent.emit()
  }

  /* istanbul ignore next */
  checkBoxAction(event, rule) {
    // Find the chamber index for this rule
    const chamberIndex = this.shearLines?.chambers?.indexOf(rule)

    // Check if user is trying to empty (uncheck) chambers 5 or 6 under special conditions
    const isTryingToEmpty = !event.checked
    const isProtectedChamber = this.shouldShowDisabledToggleForLastTwoChambers(chamberIndex)

    if (isTryingToEmpty && isProtectedChamber) {
      // Super users may empty the 5th chamber (index 4) only
      if (this.user?.isSuperUser && chamberIndex === 4) {
        rule.isEmpty = !event.checked
        this.onChange()
        return
      }
      // Let the toggle visually move first, then revert after a short delay
      setTimeout(() => {
        event.source.checked = true
        this.errorService.throw({ error: { message: "Rule 015 with 6-pin key systems does not support emptying 5th and 6th chambers" } })
      }, 250)
      return
    }

    // Normal behavior - allow the change
    rule.isEmpty = !event.checked
    this.onChange()
  }

  hasError(colIndex, rowIndex) {
    let hasError = false

    let chamber = this.shearLines.chambers[colIndex]
    if (chamber && chamber.errors) {
      chamber.errors.forEach((error) => {
        if (rowIndex >= error.startPosition && rowIndex <= error.endPosition && error.isRule) {
          hasError = true
        }
      })
    }

    return hasError
  }

  hasWarning(colIndex, rowIndex) {
    let hasWarning = false

    let chamber = this.shearLines.chambers[colIndex]
    if (chamber && chamber.errors) {
      chamber.errors.forEach((error) => {
        if (error.startPosition >= rowIndex && error.endPosition <= rowIndex && !error.isRule) {
          hasWarning = true
        }
      })
    }
    return hasWarning
  }

  isDefauldCrossKeyedCylinder() {
    return this.selectedCylinder.cylinderTypeId === 4
  }

  defauldCrossKeyedCylinderHasShearlines() {
    return (
      this.selectedCylinder?.formattedShearLines?.shearLineDepths &&
      this.selectedCylinder.formattedShearLines.shearLineDepths.length > 0
    )
  }

  /* istanbul ignore next */
  condensedErrorMessages(errors) {
    let toolTip = ''

    errors.forEach((error) => {
      toolTip = toolTip + error.errorMessage
    })

    return toolTip
  }

  /* istanbul ignore next */
  onChange() {
    this.inputUpdates.emit()
  }

  /* istanbul ignore next */
  cancel() {
    this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
    this.editing = false
  }

  /* istanbul ignore next */
  sort() {
    this.sortShearLines.emit()
  }

  /* istanbul ignore next */
  reset() {
    this.resetShearLines.emit()
  }

  /* istanbul ignore next */
  generate() {
    this.autoEdit = true
    this.generateShearLines.emit()
  }

  /* istanbul ignore next */
  move(object) {
    const inputToArray = this.inputs.toArray()
    let index = inputToArray.findIndex((x) => x.element == object.element)
    switch (object.action) {
      case 'UP':
        index -= this.columns
        break
      case 'DOWN':
        index += this.columns
        break
      case 'LEFT':
        index--
        break
      case 'RIGTH':
        index++
        break
      case 'RIGTH':
        index++
        break
    }

    if (index >= 0 && index < this.inputs.length) inputToArray[index].element.nativeElement.focus()
  }

  displayShearlineButton() {
    const nonFutureKeys = this.selectedCylinder.operators.filter((m) => !m.isFutureKey)
    const areAllFutureKeys = this.selectedCylinder.operators.every((m) => m.isFutureKey)
    const hasNonFutureKeysWithoutBitting = nonFutureKeys.some((m) => !m.bitting || m.bitting === '[N/A]')
    const allNonFutureKeysHaveBitting = nonFutureKeys.every((m) => m.bitting && m.bitting !== '[N/A]')

    return !areAllFutureKeys && (hasNonFutureKeysWithoutBitting || !allNonFutureKeysHaveBitting)
  }
}
